import ApexCharts, { ApexOptions } from "apexcharts";
import { format } from "date-fns";
import React from "react";
import ReactApexChart from "react-apexcharts";

import { SkeletonLoader } from "@fronterahealth/frontera-ui-components";

import { CumulativeTargets } from "@api/graphql/types-and-hooks";

interface ChartDataPoint {
  x: string | number;
  y: number | null;
}

interface ChartSeries {
  name: string;
  data: ChartDataPoint[];
}

export interface ZeroLineRange {
  from: Date;
  to: Date;
}

export const NO_TARGETS_MASTERED_TEXT = "No targets mastered yet";

const formatChartDate = (date: Date): string => format(date, "MM/dd/yyyy");

// One point per distinct day, so a range that starts and ends on the same day is a single point.
const zeroLinePoints = ({ from, to }: ZeroLineRange): ChartDataPoint[] => {
  const start = from > to ? to : from;
  return [...new Set([formatChartDate(start), formatChartDate(to)])].map((x) => ({ x, y: 0 }));
};

type ChartVariant = "mastered" | "zero-line";

// The backend also sends rows with a count of 0 (one per status change), so "nothing
// mastered" means every count is 0, not just an empty list.
const buildChartData = (
  cumulativeTargets: CumulativeTargets[] | undefined,
  zeroLineRange: ZeroLineRange | undefined,
): { chartData: ChartDataPoint[]; variant: ChartVariant } => {
  const masteredPoints = (cumulativeTargets ?? []).map((item) => ({
    x: formatChartDate(new Date(item.date)),
    y: item.totalCount || 0,
  }));
  if (zeroLineRange && masteredPoints.every((point) => !point.y)) {
    return { chartData: zeroLinePoints(zeroLineRange), variant: "zero-line" };
  }
  return { chartData: masteredPoints, variant: "mastered" };
};

const zeroLineTitle = (variant: ChartVariant): Pick<ApexOptions, "title"> =>
  variant === "zero-line"
    ? {
        title: {
          text: NO_TARGETS_MASTERED_TEXT,
          align: "center",
          style: { color: "#6b7280", fontSize: "14px", fontWeight: 500 },
        },
      }
    : {};

// A 0-1 axis with one tick so the zero line reads 0 and 1, not repeated labels.
const zeroLineTicks = (variant: ChartVariant): { tickAmount?: number } =>
  variant === "zero-line" ? { tickAmount: 1 } : {};

interface CumulativeTargetsChartProps {
  cumulativeTargets: CumulativeTargets[];
  chartId: string;
  /** When set and nothing is mastered, draws a flat line at 0 across this range so approval saves it. */
  zeroLineRange?: ZeroLineRange;
  isLoading?: boolean;
  forClientProgramming?: boolean;
  chartRef?: React.RefObject<ApexCharts | null>;
}

export const CumulativeTargetsChart: React.FC<CumulativeTargetsChartProps> = ({
  cumulativeTargets,
  chartId,
  zeroLineRange,
  isLoading = false,
  forClientProgramming = false,
  chartRef,
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center bg-white">
        <SkeletonLoader loading={isLoading}>
          <div className={`h-80 ${forClientProgramming ? "w-[55vw]" : "w-screen"} mx-16`}></div>
        </SkeletonLoader>
      </div>
    );
  }

  const { chartData, variant } = buildChartData(cumulativeTargets, zeroLineRange);

  // Nothing mastered and no zero-line range: show "No data yet" under a different
  // chart ID so it won't be captured on approval
  if (chartData.length === 0) {
    const emptyChartOptions: ApexOptions = {
      chart: {
        id: `${chartId}-empty`, // Different ID so approval handlers won't find it
        type: "line",
        height: forClientProgramming ? 300 : 400,
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: false },
      },
      xaxis: {
        title: { text: "Date" },
        labels: {
          formatter: () => "",
        },
      },
      yaxis: {
        min: 0,
        max: 10,
        tickAmount: 4,
        title: { text: "Total Targets Mastered" },
        labels: {
          formatter: (value) => value.toFixed(0),
        },
      },
      grid: {
        borderColor: "#e5e7eb",
        strokeDashArray: 4,
      },
      noData: {
        text: "No data yet",
        align: "center",
        verticalAlign: "middle",
        style: {
          color: "#9ca3af",
          fontSize: "16px",
        },
      },
    };

    return (
      <div className={`${!forClientProgramming ? "p-4" : ""} bg-white`}>
        <ReactApexChart
          key="no-data"
          options={emptyChartOptions}
          series={[]}
          type="line"
          height={forClientProgramming ? 300 : 400}
          chartRef={chartRef}
        />
      </div>
    );
  }

  const maxItems = Math.max(...chartData.map((point) => point.y || 0)) + 1;

  const chartOptions: ApexOptions = {
    chart: {
      id: chartId,
      type: "line",
      height: forClientProgramming ? 300 : 400,
      toolbar: { show: false },
      zoom: {
        enabled: false,
      },
      animations: {
        enabled: false,
      },
    },
    ...zeroLineTitle(variant),
    markers: {
      size: 5,
    },
    xaxis: {
      title: {
        text: "Date",
      },
    },
    yaxis: {
      title: {
        text: "Total Targets Mastered",
        offsetX: maxItems < 3 ? 10 : 0,
      },
      labels: {
        formatter: function (val) {
          return val.toFixed(0);
        },
      },
      min: 0,
      max: maxItems,
      ...zeroLineTicks(variant),
    },
  };

  const chartSeries: ChartSeries[] = [
    {
      name: "Total Targets Mastered",
      data: chartData,
    },
  ];

  return (
    <div className={`${!forClientProgramming ? "p-4" : ""} bg-white`}>
      {/* Apex merges option updates into the live chart, so a key per variant keeps the
          zero-line title and axis from carrying over when mastered data arrives. */}
      <ReactApexChart
        key={variant}
        options={chartOptions}
        series={chartSeries}
        type="line"
        height={forClientProgramming ? 300 : 400}
        chartRef={chartRef}
      />
    </div>
  );
};
